/**
 * 올린 HTML 을 가두는 규칙(2026-10-19) — 화면(플레이어)과 콘솔 미리보기가 **같은 규칙**으로 띄운다.
 *
 * 올린 HTML 은 우리 시스템 밖에서 만든 것이고, 그 안의 스크립트는 누가 짰는지 모른다. 그래서 세 겹으로 가둔다.
 *
 * 1. **출처를 빼앗는다** — `<iframe sandbox="allow-scripts" srcdoc=…>`(데스크톱 셸은 `srcdoc` 이 재생 화면의 CSP 를 물려받아
 *    스크립트가 막히므로 셸이 내주는 주소로 — 머리 `CSP: sandbox allow-scripts`, `apps/player-tauri/src/html_doc.rs`). `allow-same-origin` 이 없어 문서는 어느 출처에도
 *    속하지 않는다(불투명 출처). 부모(플레이어·콘솔)의 화면·저장소·쿠키·셸 열쇠에 손댈 수 없고, 맨 위 창을 옮기거나 새 창·
 *    대화상자·양식 제출도 못 한다. 외부 웹 페이지(`web`)와 다른 점이 이것이다 — 그쪽은 남의 출처라 `allow-same-origin` 을
 *    줘도 부모와 갈리지만, 올린 HTML 을 우리 출처로 띄우면 우리 것이 된다.
 * 2. **밖으로 나가는 길을 https 로 좁힌다** — 문서 맨 앞에 CSP 를 끼운다(`HTML_CSP`). 매장 사내망의 http 장비(공유기·
 *    POS)를 두드리거나, 플러그인을 띄우거나, 양식을 보내지 못한다. 문서 안의 CSP 는 더 좁힐 수만 있다(정책은 모두 겹쳐 걸린다).
 * 3. **권한을 주지 않는다** — `allow` 는 자동 재생뿐이다. 카메라·마이크·위치·전체 화면은 없다.
 *
 * 저장·전달 쪽도 막는다(서버): 저장소에는 `text/plain` 첨부로 넣어 어떤 주소로 열어도 **우리 출처에서 HTML 로 그려지지
 * 않는다** — 그러면 콘솔 세션을 노리는 스크립트가 된다.
 */
/** iframe 의 `sandbox` — 스크립트만. 같은 출처·맨 위 이동·새 창·양식·대화상자는 주지 않는다. */
export const HTML_SANDBOX = 'allow-scripts';
/** iframe 의 `allow`(권한 정책) — 소리 나는 자동 재생만. */
export const HTML_ALLOW = 'autoplay';
/**
 * 문서 맨 앞에 끼우는 CSP.
 *
 * 외부 자원은 https(와 문서 안의 data·blob)만. 인라인 스크립트·`eval` 은 허락한다 — 배너 제작 도구가 뽑은 HTML 은 대개 그것으로
 * 돈다. 가두는 일은 샌드박스가 하고, 이것은 나가는 길을 좁힌다.
 */
export const HTML_CSP = [
    "default-src https: data: blob:",
    "script-src https: data: blob: 'unsafe-inline' 'unsafe-eval'",
    "style-src https: data: 'unsafe-inline'",
    'img-src https: data: blob:',
    'media-src https: data: blob:',
    'font-src https: data:',
    'connect-src https: wss:',
    'frame-src https:',
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
].join('; ');
/** HTML 속성값으로 넣을 수 있게 — `"`·`&`·`<`·`>`. */
function attribute(value) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * 문서 앞머리 — 맨 앞의 공백과 `<!doctype …>`. 그 뒤가 CSP 를 끼울 자리다.
 *
 * doctype **앞에** 끼우면 문서가 호환 모드(quirks)로 떨어져 배치가 달라진다. 그렇다고 `<head>` 뒤에 끼우면, 그보다 앞에 둔
 * `<script>` 가 CSP 없이 먼저 돈다 — 그래서 "doctype 바로 뒤, 없으면 맨 앞" 이다.
 *
 * doctype 앞의 주석은 건너뛰지 않는다. 주석의 끝을 정규식으로 찾으면 브라우저와 어긋날 수 있다(`<!-->` 는 브라우저에는 이미
 * 닫힌 주석이다) — 그 틈에 둔 스크립트가 CSP 보다 먼저 돈다. 그런 문서는 맨 앞에 끼운다(호환 모드가 될 뿐 막는 것은 같다).
 */
const LEADING_DOCTYPE = /^\s*<!doctype[^>]*>/i;
/** 앞머리 스크립트를 끼운 표시 — 두 번 끼우지 않는다. */
export const COMPAT_SCRIPT_ATTR = 'data-so-compat';
/**
 * 문서의 스크립트가 저장소를 부르는 이름 — 서버가 받을 때 `localStorage` 를 `__soStorage.localStorage` 로 바꿔 둔다
 * (`apps/server/src/services/html-legacy.ts`). 5.1 웹뷰는 `window.localStorage` 자체를 바꿔 끼울 수 없어서다(아래).
 */
export const STORAGE_ALIAS = '__soStorage';
/**
 * 브라우저 저장소 대용(2026-10-30).
 *
 * 출처 없는 샌드박스에서는 `localStorage`·`sessionStorage` 가 막혀 SecurityError 를 던진다. 고른 값을 저장소에 적어 두고 다시
 * 읽는 문서(무재해 현황판처럼 시작일을 적어 두고 D-day 를 세는 것)는 그 자리에서 스크립트가 멈춰 화면에 아무것도 계산되지
 * 않는다. 그래서 **실제로 한 번 적어 보고**, 안 되면 문서 하나가 사는 동안의 메모리 저장소를 쓴다 — 화면은 항목을 틀 때마다
 * 문서를 새로 띄우므로 적은 값은 다음 차례에 남지 않는다. 쓸 수 있는 곳(같은 출처를 준 틀)은 그 저장소를 그대로 쓴다.
 *
 * 쓰는 길은 둘이다. ① `window.localStorage` 를 메모리 저장소로 바꿔 끼운다 — 요즘 브라우저에서 된다. ② `__soStorage` 에도
 * 둔다 — 5.1 웹뷰(Chromium 39)는 그 속성을 읽기·지우기·다시 정의하기가 모두 SecurityError 라 ①이 안 된다(롤리팝 에뮬레이터
 * 실측). 서버가 받을 때 문서의 스크립트가 ②를 부르게 바꿔 둔다.
 */
export const HTML_STORAGE_SHIM = [
    '(function () {',
    '  function memory() {',
    '    var data = {};',
    '    var storage = {',
    '      getItem: function (key) { key = String(key); return Object.prototype.hasOwnProperty.call(data, key) ? data[key] : null; },',
    '      setItem: function (key, value) { data[String(key)] = String(value); },',
    '      removeItem: function (key) { delete data[String(key)]; },',
    '      clear: function () { data = {}; },',
    '      key: function (index) { var keys = Object.keys(data); return index >= 0 && index < keys.length ? keys[index] : null; }',
    '    };',
    "    Object.defineProperty(storage, 'length', { get: function () { return Object.keys(data).length; } });",
    '    return storage;',
    '  }',
    "  var names = ['localStorage', 'sessionStorage'], safe = {};",
    '  for (var i = 0; i < names.length; i++) {',
    '    var name = names[i], store = null;',
    "    try { store = window[name]; store.setItem('__so_probe__', '1'); store.removeItem('__so_probe__'); } catch (error) { store = null; }",
    '    if (!store) {',
    '      store = memory();',
    '      try { Object.defineProperty(window, name, { value: store, configurable: true, enumerable: true }); } catch (error) {}',
    '    }',
    '    safe[name] = store;',
    '  }',
    `  window.${STORAGE_ALIAS} = safe;`,
    '})();',
].join('\n');
/**
 * 5.1 웹뷰(Chromium 39)에 없는 기본 함수(2026-10-30) — 요즘 손으로 짠 스크립트가 흔히 쓰는 것만. 문법(구조 분해·화살표 함수 …)은
 * 여기서 못 고친다 — 서버가 받을 때 ES5 로 바꿔 둔다(`apps/server/src/services/html-legacy.ts`). 있는 것은 덮지 않는다.
 */
export const HTML_POLYFILLS = [
    '(function () {',
    '  function define(target, name, value) {',
    '    if (!target || !value || name in target) return;',
    '    try { Object.defineProperty(target, name, { value: value, configurable: true, writable: true }); } catch (error) {}',
    '  }',
    '  function pad(text, length, fill, atStart) {',
    "    text = String(text); length = length | 0; fill = fill === undefined ? ' ' : String(fill);",
    "    if (length <= text.length || fill === '') return text;",
    "    var need = length - text.length, padding = '';",
    '    while (padding.length < need) padding += fill;',
    '    padding = padding.slice(0, need);',
    '    return atStart ? padding + text : text + padding;',
    '  }',
    '  var S = String.prototype, A = Array.prototype;',
    '  define(S, "includes", function (search, start) { return String(this).indexOf(search, start) !== -1; });',
    '  define(S, "startsWith", function (search, at) { at = at > 0 ? at | 0 : 0; search = String(search); return String(this).substring(at, at + search.length) === search; });',
    '  define(S, "endsWith", function (search, end) { var text = String(this); search = String(search); end = end === undefined || end > text.length ? text.length : end | 0; return text.substring(end - search.length, end) === search; });',
    "  define(S, \"repeat\", function (count) { var text = String(this), out = ''; count = count | 0; if (count < 0) throw new RangeError('Invalid count value'); while (count-- > 0) out += text; return out; });",
    '  define(S, "padStart", function (length, fill) { return pad(this, length, fill, true); });',
    '  define(S, "padEnd", function (length, fill) { return pad(this, length, fill, false); });',
    "  define(S, \"trimStart\", function () { return String(this).replace(/^\\s+/, ''); });",
    "  define(S, \"trimEnd\", function () { return String(this).replace(/\\s+$/, ''); });",
    '  define(A, "includes", function (value, from) { var list = Object(this), n = list.length >>> 0, i = from | 0; if (i < 0) i = Math.max(n + i, 0); for (; i < n; i++) { var x = list[i]; if (x === value || (x !== x && value !== value)) return true; } return false; });',
    '  define(A, "find", function (test, self) { var list = Object(this), n = list.length >>> 0; for (var i = 0; i < n; i++) if (test.call(self, list[i], i, list)) return list[i]; return undefined; });',
    '  define(A, "findIndex", function (test, self) { var list = Object(this), n = list.length >>> 0; for (var i = 0; i < n; i++) if (test.call(self, list[i], i, list)) return i; return -1; });',
    '  define(A, "fill", function (value, start, end) { var list = Object(this), n = list.length >>> 0, i = start | 0, e = end === undefined ? n : end | 0; if (i < 0) i = Math.max(n + i, 0); if (e < 0) e = Math.max(n + e, 0); for (; i < e && i < n; i++) list[i] = value; return list; });',
    "  define(Array, \"from\", function (items, map, self) { if (items == null) throw new TypeError('Array.from requires an array-like object'); var out = [], i; var iterate = typeof Symbol === 'function' && Symbol.iterator && items[Symbol.iterator]; if (typeof iterate === 'function') { var it = iterate.call(items), step; while (!(step = it.next()).done) out.push(step.value); } else { var list = Object(items), n = list.length >>> 0; for (i = 0; i < n; i++) out.push(list[i]); } if (map) for (i = 0; i < out.length; i++) out[i] = map.call(self, out[i], i); return out; });",
    '  define(Array, "of", function () { return A.slice.call(arguments); });',
    "  define(Object, \"assign\", function (target) { if (target == null) throw new TypeError('Cannot convert undefined or null to object'); var to = Object(target); for (var i = 1; i < arguments.length; i++) { var from = arguments[i]; if (from != null) for (var key in from) if (Object.prototype.hasOwnProperty.call(from, key)) to[key] = from[key]; } return to; });",
    '  define(Object, "values", function (object) { return Object.keys(object).map(function (key) { return object[key]; }); });',
    '  define(Object, "entries", function (object) { return Object.keys(object).map(function (key) { return [key, object[key]]; }); });',
    '  if (window.NodeList) define(NodeList.prototype, "forEach", A.forEach);',
    '  var E = window.Element && Element.prototype;',
    '  if (E) {',
    '    define(E, "matches", E.webkitMatchesSelector || E.msMatchesSelector);',
    '    define(E, "closest", function (selector) { var node = this; while (node && node.nodeType === 1) { if (node.matches(selector)) return node; node = node.parentNode; } return null; });',
    '  }',
    '})();',
].join('\n');
/** 문서 맨 앞에 끼우는 스크립트 — 저장소 대용과 기본 함수. ES5 로 적는다(5.1 웹뷰). */
export const HTML_COMPAT_SCRIPT = `${HTML_STORAGE_SHIM}\n${HTML_POLYFILLS}`;
/**
 * 앞머리 스크립트를 문서 맨 앞에 끼운다 — 문서의 어떤 스크립트보다 먼저 돌아야 한다. 이미 있으면 그대로.
 *
 * 서버는 HTML 을 받을 때 끼워 저장한다(지금 깔린 화면도 바로 돈다). 화면·콘솔도 띄울 때 끼운다(`sandboxedHtml`) — 그 전에
 * 올린 파일도 돈다.
 */
export function withCompatScript(html) {
    if (html.includes(COMPAT_SCRIPT_ATTR))
        return html;
    return prependToHtml(html, `<script ${COMPAT_SCRIPT_ATTR}>\n${HTML_COMPAT_SCRIPT}\n</script>`);
}
/**
 * 올린 HTML 에 CSP 와 앞머리 스크립트를 끼운 문서 — `srcdoc` 에 넣는다.
 *
 * 끼운 `<meta>` 는 문서의 첫 요소라 파서가 `<head>` 안에 둔다. 문서가 제 CSP 를 더 갖고 있어도 둘 다 걸린다(더 좁아질 뿐이다).
 * 앞머리 스크립트는 그 바로 뒤 — CSP 아래에서 문서의 스크립트보다 먼저 돈다.
 */
export function sandboxedHtml(html, options = {}) {
    return prependToHtml(withCompatScript(html), `<meta http-equiv="Content-Security-Policy" content="${attribute(htmlCsp(options.offline))}">`);
}
/**
 * 바깥 연결을 막은 고객사의 CSP(2026-11-03, 환경 설정의 `html.offline`) — 문서 안의 것(인라인·`data:`·`blob:`)만 쓰고 바깥
 * 주소는 아무것도 부르지 않는다. 폐쇄망 현장은 잃는 것이 없고, 승인한 뒤에 바깥 파일이 바뀌어 화면이 바뀌는 일이 없다. 품은
 * 영상은 칸 밖 겹층에서 플레이어가 트므로(`html-media.ts`) 막혀도 나온다.
 */
export const HTML_CSP_OFFLINE = [
    'default-src data: blob:',
    "script-src data: blob: 'unsafe-inline' 'unsafe-eval'",
    "style-src data: 'unsafe-inline'",
    'img-src data: blob:',
    'media-src data: blob:',
    'font-src data:',
    "connect-src 'none'",
    'frame-src data: blob:',
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
].join('; ');
/** 화면·미리보기에 거는 CSP — 바깥 연결을 막았으면 `HTML_CSP_OFFLINE`, 아니면 https 만 여는 `HTML_CSP`. */
export function htmlCsp(offline = false) {
    return offline ? HTML_CSP_OFFLINE : HTML_CSP;
}
/** 문서 맨 앞(doctype 바로 뒤, 없으면 맨 앞)에 끼운다 — 이유는 `LEADING_DOCTYPE`. */
export function prependToHtml(html, snippet) {
    const text = html.replace(/^\uFEFF/, '');
    const head = LEADING_DOCTYPE.exec(text);
    if (!head)
        return snippet + text;
    return head[0] + snippet + text.slice(head[0].length);
}
//# sourceMappingURL=html-sandbox.js.map